import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/Button';
import { Icon } from '@/shared/components/Icon';
import { cn } from '@/shared/utils/cn';

import { useGuidanceInternals, type GuidanceInternals } from '../context';
import { HELP_PANEL_ID } from '../engine/dom';
import { excludedRouteFor } from '../safeRoutes';
import { useGuidanceState } from '../store';

export interface HelpEntryProps {
  /** Merged last, so a shell can restyle it — the operator's dark header, say. */
  className?: string;
}

/**
 * The "Help & tours" entry («المساعدة والجولات الإرشادية») for a shell's
 * header, inside `GuidanceRoot`, before the language toggle.
 *
 * - A real button that opens the Help panel; icon-only below 640px, where its
 *   accessible name stays "Help & tours".
 * - It tags itself `<portal>.shell.help`, so the shell does not.
 * - It renders nothing outside a `GuidanceRoot` — so never on sign-in, MFA,
 *   activation or a secure link — and nothing on a page inside a shell that
 *   shows or collects a secret (`/account/security`, `/platform/security`).
 */
export function HelpEntry({ className }: HelpEntryProps) {
  const { t } = useTranslation();
  const guidance = useGuidanceInternals();

  if (guidance === null || excludedRouteFor(guidance.ctx.pathname)?.currentPageGuide === false)
    return null;

  return <HelpButton guidance={guidance} className={className} label={t('guidance.help.entry')} />;
}

function HelpButton({
  guidance,
  className,
  label,
}: {
  guidance: GuidanceInternals;
  className?: string;
  label: string;
}) {
  const { panelOpen } = useGuidanceState(guidance.store);

  return (
    <Button
      ref={guidance.store.setHelpEntry}
      variant="ghost"
      size="sm"
      data-tour={`${guidance.portal}.shell.help`}
      aria-haspopup="dialog"
      aria-expanded={panelOpen}
      aria-controls={HELP_PANEL_ID}
      onClick={() => guidance.store.openPanel()}
      className={cn('min-w-11 justify-center gap-1.5', className)}
    >
      <Icon name="help" size={18} className="shrink-0" />
      <span className="max-sm:sr-only">{label}</span>
    </Button>
  );
}
